import axios from 'axios';

import { BASE_URL } from '../configs/envs';

export enum HttpMethod {
  Get = 'GET',
  Post = 'POST',
  Put = 'PUT',
  Patch = 'PATCH',
  Delete = 'DELETE',
}

const DEFAULT_TIMEOUT_MS = 15000;

export const httpClient = <T = unknown>(
  url: string,
  method: HttpMethod,
  data?: unknown,
  headers?: Record<string, string>,
  timeoutMs?: number,
  signal?: AbortSignal
): Promise<T> => {
  return new Promise((resolve, reject) => {
    const params: {
      method: HttpMethod;
      url: string;
      timeout: number;
      data?: unknown;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    } = {
      method,
      url: `${BASE_URL}${url}`,
      timeout: timeoutMs ?? DEFAULT_TIMEOUT_MS,
    };
    if (data) {
      params.data = data;
    }

    if (headers) {
      params.headers = headers;
    }

    if (signal) {
      params.signal = signal;
    }

    axios(params)
      .then((response) => {
        if (response.status > 199 && response.status < 300) {
          resolve(response.data);
        } else {
          reject('Response is not success');
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * GET request helper. Skips the `data` / `headers` / `timeoutMs` positional
 * slots so queryFn call sites can pass just the URL and a React Query signal:
 *
 *   queryFn: ({ signal }) => httpGet<Thing>(`/thing/${id}`, signal)
 *
 * For non-GET methods, or when you need headers / custom timeout, keep using
 * `httpClient` directly.
 */
export const httpGet = <T = unknown>(url: string, signal?: AbortSignal): Promise<T> =>
  httpClient<T>(url, HttpMethod.Get, undefined, undefined, undefined, signal);

/**
 * POST helper that mirrors `httpGet` for signal-aware call sites — avoids
 * threading `undefined` through the `headers` / `timeoutMs` slots when all a
 * caller wants is a URL + body + React Query signal.
 */
export const httpPost = <T = unknown>(
  url: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> => httpClient<T>(url, HttpMethod.Post, body, undefined, undefined, signal);
